import { toast } from '@/hooks/useToast';

/** The subset of Clerk's `setActive` this needs: switch the session's active organization. */
type SetActiveOrganization = (params: {
  organization: string;
  redirectUrl?: string;
}) => Promise<unknown>;

export const ORGANIZATION_SWITCH_DEADLINE_MS = 10_000;

function dashboardUrl(): string {
  return new URL('/dashboard', window.location.origin).toString();
}

/**
 * Sets the Clerk session's active organization, reporting failure instead of throwing.
 *
 * Returns false (and shows a toast) when Clerk rejects the switch — e.g. the organization was
 * removed from Clerk after our list was read, or the caller is not a member. Without this, a
 * failed setActive throws an uncaught rejection and the caller silently does nothing, which
 * leaves a signed-in user with no active organization and no signal that anything went wrong.
 *
 * Deliberately does NOT navigate. Callers that run from a background effect on any route need
 * the guard without the page change; `switchActiveOrganization` adds the navigation for the
 * explicit switcher click.
 */
export async function setActiveOrganization(
  setActive: SetActiveOrganization,
  clerkOrganizationId: string,
  redirectUrl?: string
): Promise<boolean> {
  let deadline: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      setActive({
        organization: clerkOrganizationId,
        ...(redirectUrl ? { redirectUrl } : {}),
      }),
      new Promise<never>((_, reject) => {
        deadline = setTimeout(
          () => reject(new Error('organization switch timed out')),
          ORGANIZATION_SWITCH_DEADLINE_MS
        );
      }),
    ]);
    return true;
  } catch {
    toast.error(
      'Could not switch organization',
      'It may no longer exist or you may not have access. Try refreshing.'
    );
    return false;
  } finally {
    clearTimeout(deadline);
  }
}

/**
 * Switches the Clerk session to `clerkOrganizationId`, then lands on that organization's dashboard via a
 * full navigation. The full load (not an SPA transition) is what makes every organization-scoped
 * query refetch with the new active-organization token — without it the switch is a silent no-op
 * showing the previous organization's cached data. Going to /dashboard (rather than reloading the
 * current page) also lets the organization switcher act as an escape hatch from /onboarding: a
 * multi-organization user redirected there can switch to a working organization and land on its dashboard.
 *
 * The navigation is the whole point of this wrapper, so it belongs only to caller-initiated
 * switches. A hook that runs on every route must call `setActiveOrganization` directly, or it
 * would yank users off whatever page they are on.
 */
export async function switchActiveOrganization(
  setActive: SetActiveOrganization,
  clerkOrganizationId: string
): Promise<boolean> {
  const nextUrl = dashboardUrl();
  if (!(await setActiveOrganization(setActive, clerkOrganizationId, nextUrl))) {
    return false;
  }
  window.location.assign(nextUrl);
  return true;
}
